// components/UndoRedoControls.js
import React, { useState, useEffect } from 'react';
import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@material-ui/lab';
import {
  Undo,
  Redo,
  History,
  Save,
  Restore
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip, Badge } from '@material-ui/core';
import { scheduleHistory } from '../utils/scheduleHistory';

const useStyles = makeStyles((theme) => ({
  speedDial: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1200
  }
}));

const UndoRedoControls = ({ 
  onUndo, 
  onRedo, 
  onShowHistory,
  onSaveCheckpoint 
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [changeCount, setChangeCount] = useState(0);

  // Check history state
  useEffect(() => {
    const checkHistoryState = () => {
      const history = scheduleHistory.getHistory();
      setChangeCount(history.length);
      
      // Check if we can undo (have history)
      setCanUndo(history.length > 0);
      
      // For redo, we'd need to track forward history
      // This is a simplified version
      setCanRedo(false);
    };

    checkHistoryState();
    
    // Set up an interval to check for changes
    const interval = setInterval(checkHistoryState, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const handleUndo = () => {
    const history = scheduleHistory.getHistory();
    if (history.length > 0) {
      const lastChange = history[0];
      if (onUndo) {
        onUndo(lastChange);
      }
    }
    setOpen(false);
  };

  const handleRedo = () => {
    // Implement redo logic
    if (onRedo) {
      onRedo();
    }
    setOpen(false);
  };

  const handleShowHistory = () => {
    if (onShowHistory) {
      onShowHistory();
    }
    setOpen(false);
  };

  const handleSaveCheckpoint = () => {
    if (onSaveCheckpoint) {
      onSaveCheckpoint();
    }
    setOpen(false);
  };

  const actions = [
    {
      icon: <Save />,
      name: 'Save Checkpoint',
      onClick: handleSaveCheckpoint,
      disabled: false
    },
    {
      icon: (
        <Badge badgeContent={changeCount} color="primary">
          <History />
        </Badge>
      ),
      name: 'View History',
      onClick: handleShowHistory,
      disabled: false
    },
    {
      icon: <Redo />,
      name: 'Redo',
      onClick: handleRedo,
      disabled: !canRedo
    },
    {
      icon: <Undo />,
      name: 'Undo Last Change',
      onClick: handleUndo,
      disabled: !canUndo
    }
  ];

  return (
    <SpeedDial
      ariaLabel="Undo/Redo Controls"
      className={classes.speedDial}
      icon={<SpeedDialIcon icon={<Restore />} />}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      direction="up"
    >
      {actions.map((action) => (
        <SpeedDialAction
          key={action.name}
          icon={action.icon}
          tooltipTitle={action.name}
          tooltipOpen
          onClick={action.onClick}
          disabled={action.disabled}
        />
      ))}
    </SpeedDial>
  );
};

export default UndoRedoControls;